import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import { ProfileImageUpload, SpeakerToggle } from '../components';

interface UserProfile {
  user_id: string;
  username: string;
  name: string;
  email: string;
  description?: string;
  role?: string;
  profile_complete: boolean;
  profile_image_url?: string;
  is_speaker: boolean;
  talk_count?: number;
  status: string;
  created_at: string;
  updated_at: string;
}

const ProfilePage: React.FC = () => {
  const { refreshToken } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_speaker: false,
    emailNotifications: true
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading profile...');
      const response = await apiClient.get('/v1/profile');
      console.log('Profile API response:', response.data);

      const profileData = response.data.data.profile;
      console.log('Extracted profile data:', profileData);

      setProfile(profileData);
      setFormData({
        name: profileData.name || '',
        description: profileData.description || '',
        is_speaker: profileData.is_speaker || false,
        emailNotifications: true // Default value since API doesn't return this
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
      console.error('Error details:', err);
      setError('Failed to load profile information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    // Reset form data to current profile values
    if (profile) {
      setFormData({
        name: profile.name || '',
        description: profile.description || '',
        is_speaker: profile.is_speaker || false,
        emailNotifications: true // Default value since API doesn't return this
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Name is required');
      }

      console.log('Saving profile with data:', {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_speaker: formData.is_speaker,
        emailNotifications: formData.emailNotifications
      });

      // Update profile
      const response = await apiClient.put('/v1/profile', {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_speaker: formData.is_speaker
      });

      console.log('Profile save response:', response.data);
      const updatedProfile = response.data.data.profile;
      console.log('Updated profile data:', updatedProfile);

      setProfile(updatedProfile);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');

      // Refresh auth token to get updated user info
      await refreshToken();
    } catch (err) {
      console.error('Failed to update profile:', err);
      console.error('Error details:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Loading Profile...</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <i className="fas fa-user me-2"></i>
                My Profile
              </h4>
              {!isEditing && (
                <button
                  className="btn btn-outline-primary"
                  onClick={handleEdit}
                >
                  <i className="fas fa-edit me-1"></i>
                  Edit Profile
                </button>
              )}
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  {success}
                </div>
              )}

              {profile && (
                <>
                  {/* Profile Image Upload */}
                  <ProfileImageUpload
                    currentImageUrl={profile.profile_image_url}
                    onImageUpdate={(imageUrl) => {
                      setProfile(prev => prev ? { ...prev, profile_image_url: imageUrl || undefined } : null);
                    }}
                    className="mb-4"
                  />

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Full Name</label>
                        {isEditing ? (
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        ) : (
                          <p className="form-control-plaintext">{profile.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Email Address</label>
                        <p className="form-control-plaintext">{profile.email}</p>
                        <small className="text-muted">Email cannot be changed here</small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">About</label>
                    {isEditing ? (
                      <textarea
                        className="form-control"
                        name="description"
                        rows={4}
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Tell us about yourself, your expertise, speaking experience..."
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {profile.description || (
                          <em className="text-muted">No description provided</em>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Role</label>
                    <div className="form-control-plaintext">
                      <span className={`badge ${profile.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                        {profile.role ? profile.role.toUpperCase() : 'USER'}
                      </span>
                    </div>
                  </div>

                  <SpeakerToggle
                    isSpeaker={formData.is_speaker}
                    talkCount={profile.talk_count || 0}
                    isEditing={isEditing}
                    onChange={(enabled) => setFormData(prev => ({ ...prev, is_speaker: enabled }))}
                  />

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailNotifications"
                        name="emailNotifications"
                        checked={formData.emailNotifications}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                      <label className="form-check-label fw-bold" htmlFor="emailNotifications">
                        Email Notifications
                      </label>
                    </div>
                    <small className="text-muted ms-4">
                      Receive email notifications for event invitations, session updates, and new messages
                    </small>
                  </div>

                  {isEditing && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleCancel}
                        disabled={isSaving}
                      >
                        <i className="fas fa-times me-1"></i>
                        Cancel
                      </button>
                    </div>
                  )}

                  <hr className="my-4" />

                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="fw-bold">Account Information</h6>
                      <ul className="list-unstyled">
                        <li>
                          <strong>Profile Status:</strong>{' '}
                          <span className={`badge ${profile.profile_complete ? 'bg-success' : 'bg-warning'}`}>
                            {profile.profile_complete ? 'Complete' : 'Incomplete'}
                          </span>
                        </li>
                        <li><strong>Member Since:</strong> {new Date(profile.created_at).toLocaleDateString()}</li>
                        <li><strong>Last Updated:</strong> {new Date(profile.updated_at).toLocaleDateString()}</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6 className="fw-bold">Quick Stats</h6>
                      <ul className="list-unstyled">
                        <li><strong>Talks Created:</strong> 0</li>
                        <li><strong>Sessions Delivered:</strong> 0</li>
                        <li><strong>Events Organized:</strong> 0</li>
                      </ul>
                      <small className="text-muted">
                        Statistics will be available once you start creating content
                      </small>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
